import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import { useTableConfig } from '../table-config';
/** Valore che nell'URL diventa "parametro assente". `false` e `0` restano. */
export function isEmptySearchValue(value) {
    return (value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0));
}
/**
 * Uguaglianza strutturale fra due valori di filtro: un valore ricostruito
 * dall'URL è un oggetto nuovo a ogni lettura, e con l'uguaglianza per
 * riferimento il campo verrebbe riscritto di continuo. Vuoti equivalenti
 * (`null`, `undefined`, `''`, `[]`) sono lo stesso valore.
 */
export function isSameSearchValue(a, b) {
    if (a === b)
        return true;
    if (isEmptySearchValue(a) || isEmptySearchValue(b)) {
        return isEmptySearchValue(a) && isEmptySearchValue(b);
    }
    return JSON.stringify(a) === JSON.stringify(b);
}
function sameNameCodec(name) {
    return {
        keys: [name],
        decode: (search) => isEmptySearchValue(search[name]) ? undefined : search[name],
        encode: (value) => ({
            [name]: isEmptySearchValue(value) ? undefined : value,
        }),
    };
}
export function toSearchParamsFields(fields) {
    return Object.entries(fields).map(([name, spec]) => ({
        name,
        codec: spec === true ? sameNameCodec(name) : spec,
    }));
}
/** URL → valori del form; il campo senza valore vale `null`. */
export function decodeSearchParams(fields, search) {
    return Object.fromEntries(fields.map(({ name, codec }) => [name, codec.decode(search) ?? null]));
}
/** Valori del form → la patch dei parametri, con i vuoti a `undefined`. */
export function encodeSearchParams(fields, values) {
    return Object.fromEntries(fields.flatMap(({ name, codec }) => Object.entries(codec.encode(values[name])).map(([key, value]) => [
        key,
        isEmptySearchValue(value) ? undefined : value,
    ])));
}
/** `true` se l'URL contiene già la patch: navigare non cambierebbe nulla. */
export function isSearchPatchApplied(patch, search) {
    return Object.entries(patch).every(([key, value]) => isSameSearchValue(value, search[key]));
}
export function countActiveSearchParams(fields, search) {
    return fields.filter(({ codec }) => {
        const value = codec.decode(search);
        return !isEmptySearchValue(value) && value !== false;
    }).length;
}
/** La patch che toglie dall'URL tutti i parametri dei campi. */
export function clearedSearchParams(fields) {
    return Object.fromEntries(fields.flatMap(({ codec }) => codec.keys.map((key) => [key, undefined])));
}
/** Solo i parametri letti dai campi, in forma confrontabile fra render. */
function searchSliceKey(fields, search) {
    return JSON.stringify(Object.fromEntries(fields.flatMap(({ codec }) => codec.keys.map((key) => [key, search[key] ?? null]))));
}
function withPagination(patch, pagination) {
    return pagination
        ? { ...patch, skip: pagination.skip, take: pagination.take }
        : patch;
}
/**
 * Form di filtri sincronizzato con i parametri di ricerca della rotta.
 *
 * L'URL è la fonte di verità: il form ne è la proiezione. URL → form scrive
 * solo i campi strutturalmente diversi, con un ref che zittisce l'eco verso
 * l'URL; form → URL fa una sola `navigate` (`replace`, paginazione azzerata)
 * e solo se la patch cambia davvero l'indirizzo. Il campo svuotato vale
 * `null`, mai `undefined`: react-hook-form risolverebbe `undefined` col
 * default di montaggio e rimetterebbe a video il filtro appena tolto.
 */
export function useSearchParamsForm(options) {
    const { search, navigation, resetPagination = true } = options;
    const navigate = useNavigate();
    const { pagination } = useTableConfig();
    const fields = toSearchParamsFields(options.fields);
    const fieldsKey = JSON.stringify(fields.map(({ name, codec }) => [name, codec.keys]));
    const sliceKey = searchSliceKey(fields, search);
    const latestRef = useRef({ fields, search, navigation });
    latestRef.current = { fields, search, navigation };
    const paginationRef = useRef(resetPagination ? pagination : undefined);
    paginationRef.current = resetPagination ? pagination : undefined;
    const decoded = useMemo(() => decodeSearchParams(latestRef.current.fields, JSON.parse(sliceKey)), [sliceKey, fieldsKey]);
    const form = useForm({
        defaultValues: decoded,
    });
    const navigateWith = useCallback((patch) => {
        const { navigation: target } = latestRef.current;
        const search = (old) => ({
            ...old,
            ...withPagination(patch, paginationRef.current),
        });
        // Senza rotta si resta su quella corrente: per il router `'.'` è lo
        // stesso `to` assente, risolto dall'ultima rotta del match.
        navigate({
            to: target?.to ?? '.',
            ...(target?.params && { params: target.params }),
            search,
            replace: true,
        });
    }, [navigate]);
    const isSyncingFromUrlRef = useRef(false);
    useEffect(() => {
        isSyncingFromUrlRef.current = true;
        try {
            for (const [name, value] of Object.entries(decoded)) {
                const fieldName = name;
                if (isSameSearchValue(form.getValues(fieldName), value))
                    continue;
                form.setValue(fieldName, value, {
                    shouldDirty: false,
                });
            }
        }
        finally {
            isSyncingFromUrlRef.current = false;
        }
    }, [decoded, form]);
    useEffect(() => form.subscribe({
        formState: { values: true },
        callback: ({ values }) => {
            if (isSyncingFromUrlRef.current)
                return;
            const { fields: currentFields, search: currentSearch } = latestRef.current;
            const patch = encodeSearchParams(currentFields, values);
            if (isSearchPatchApplied(patch, currentSearch))
                return;
            navigateWith(patch);
        },
    }), [form, navigateWith]);
    const activeFilterCount = useMemo(() => countActiveSearchParams(latestRef.current.fields, JSON.parse(sliceKey)), [sliceKey, fieldsKey]);
    const clearFilters = useCallback(() => {
        const { fields: currentFields, search: currentSearch } = latestRef.current;
        const patch = clearedSearchParams(currentFields);
        if (isSearchPatchApplied(patch, currentSearch))
            return;
        navigateWith(patch);
    }, [navigateWith]);
    const applyValues = useCallback((values) => {
        const { fields: currentFields, search: currentSearch } = latestRef.current;
        const patch = encodeSearchParams(currentFields, values);
        if (isSearchPatchApplied(patch, currentSearch))
            return;
        navigateWith(patch);
    }, [navigateWith]);
    return {
        form,
        activeFilterCount,
        hasActiveFilters: activeFilterCount > 0,
        clearFilters,
        appliedValues: decoded,
        applyValues,
    };
}
//# sourceMappingURL=use-search-params-form.js.map